import { modelFactory } from '~base/ModelFactory';
import ApiHelper from '~base/api/ApiHelper';
import AttachedFilesApiRepository from '~modules/attachedFiles/AttachedFilesApiRepository';
import {
  AttachedImagesDeletionForm,
  AttachedImagesDeletionFormField,
} from '~modules/attachedFiles/forms/AttachedImagesDeletionForm';
import { AttachedFileModel } from '~modules/attachedFiles/models/AttachedFileModel';
import { UploadedFileModel } from '~modules/attachedFiles/models/UploadedFileModel';
import { UploadingStateEnum } from '~modules/attachedFiles/types/UploadingStateEnum';
import { FileModeEnum } from '~modules/fileTypes/types/FileModeEnum';

export default class AttachedFilesService {
  api: AttachedFilesApiRepository;

  constructor() {
    this.api = new AttachedFilesApiRepository();
  }

  // API

  getAttachedDocuments = async (
    mode: FileModeEnum,
    documentTypeId: number,
    documentId: number | undefined,
    documentTitle: string | undefined,
    fixedTypeId?: number | null,
  ) => {
    const params = ApiHelper.getURLSearchParams(
      ApiHelper.getTrimStringValues({
        mode,
        documentTypeId,
        fixedTypeId,
        documentId,
        documentId2: documentTitle,
      }),
    );

    const { data } = await this.api.getAttachedDocuments(params);

    return modelFactory.createList(AttachedFileModel, data.data);
  };

  addAttachedDocument = async (
    documentTypeId: number,
    documentId: number | undefined,
    documentTitle: string | undefined,
    fixedTypeId: number | null | undefined,
    file: File,
  ) => {
    const formData = new FormData();
    formData.append('DocumentTypeId', String(documentTypeId));
    formData.append('File', file);

    if (documentId) {
      formData.append('DocumentId', String(documentId));
    }

    if (documentTitle) {
      formData.append('DocumentId2', String(documentTitle));
    }

    if (fixedTypeId) {
      formData.append('FixedTypeId', String(fixedTypeId));
    }

    const { data } = await this.api.addAttachedDocument(formData);

    return modelFactory.create(AttachedFileModel, data?.data);
  };

  deleteAttachedDocument = async (fileId: number) => {
    const params = ApiHelper.getURLSearchParams(
      ApiHelper.getTrimStringValues({
        fileId,
      }),
    );

    await this.api.deleteAttachedDocument(params);
  };

  deleteAttachedDocuments = async (form: AttachedImagesDeletionForm) => {
    const fileIds = this.prepareFileIdsToDeletion(form);

    const params = ApiHelper.getURLSearchParams(
      ApiHelper.getTrimStringValues({
        fileIds,
      }),
    );

    await this.api.deleteAttachedDocuments(params);
  };

  // OTHERS

  // Подготавливает массив прикрепленных файлов к локальному стейту, добавляется uploadingState: LoadingStateEnum.SUCCESS
  prepareUploadedFiles = (attachedFiles: AttachedFileModel[]) => {
    return modelFactory.createList(UploadedFileModel, attachedFiles);
  };

  // Добавляет к локальному стейту модели новых файлов в состоянии uploadingState: LoadingStateEnum.LOADING
  addLoadedFiles = (uploadedFiles: UploadedFileModel[], newFiles: File[], fileIdMap: Map<File, number>) => {
    const tempFiles = newFiles.map((file) => ({
      fileId: fileIdMap.get(file)!,
      uploadingState: UploadingStateEnum.LOADING,
    }));

    return [...uploadedFiles, ...modelFactory.createList(UploadedFileModel, tempFiles)];
  };

  // При успешной отправке заменяет временную модель файла в состоянии uploadingState: LoadingStateEnum.LOADING
  // на модель, которую вернул API с состоянием uploadingState: LoadingStateEnum.SUCCESS
  replaceUploadedFile = (uploadedFiles: UploadedFileModel[], tempFileId: number, data: AttachedFileModel) => {
    return uploadedFiles.map((uploadedFile) => {
      if (uploadedFile.fileId === tempFileId) {
        return modelFactory.create(UploadedFileModel, data);
      }

      return uploadedFile;
    });
  };

  // При неудачной отправке заменяет временную модель файла в состоянии uploadingState: LoadingStateEnum.LOADING
  // на модель с состоянием uploadingState: LoadingStateEnum.ERROR
  replaceCaughtFile = (uploadedFiles: UploadedFileModel[], tempFileId: number) => {
    return uploadedFiles.map((uploadedFile) => {
      if (uploadedFile.fileId === tempFileId) {
        return modelFactory.create(UploadedFileModel, {
          ...uploadedFile,
          uploadingState: UploadingStateEnum.ERROR,
          isCanDelete: true,
        });
      }

      return uploadedFile;
    });
  };

  // Устанавливает состояние загрузки перед удалением файла
  setFileDeleting = (uploadedFiles: UploadedFileModel[], file: UploadedFileModel) => {
    return uploadedFiles.map((uploadedFile) => {
      if (uploadedFile.fileId === file.fileId) {
        return modelFactory.create(UploadedFileModel, {
          ...uploadedFile,
          uploadingState: UploadingStateEnum.LOADING,
        });
      }

      return uploadedFile;
    });
  };

  // Устанавливает состояние загрузки перед удалением нескольких файлов
  setFilesDeleting = (uploadedFiles: UploadedFileModel[], fileIds: number[]) => {
    return uploadedFiles.map((uploadedFile) => {
      if (uploadedFile.fileId && fileIds.includes(uploadedFile.fileId)) {
        return modelFactory.create(UploadedFileModel, {
          ...uploadedFile,
          uploadingState: UploadingStateEnum.LOADING,
        });
      }

      return uploadedFile;
    });
  };

  // Восстанавливает файл в исходное состояние (используется при ошибке удаления)
  restoreFileToOriginalState = (uploadedFiles: UploadedFileModel[], file: UploadedFileModel) => {
    return uploadedFiles.map((uploadedFile) => {
      if (uploadedFile.fileId === file.fileId) {
        return modelFactory.create(UploadedFileModel, {
          ...file,
          uploadingState: UploadingStateEnum.SUCCESS,
        });
      }

      return uploadedFile;
    });
  };

  // Восстанавливает несколько файлов в исходное состояние (используется при ошибке массового удаления)
  restoreFilesToOriginalState = (uploadedFiles: UploadedFileModel[], filesToRestore: UploadedFileModel[]) => {
    const filesMap = new Map(filesToRestore.map((file) => [file.fileId, file]));

    return uploadedFiles.map((uploadedFile) => {
      if (uploadedFile.fileId && filesMap.has(uploadedFile.fileId)) {
        const originalFile = filesMap.get(uploadedFile.fileId)!;
        return modelFactory.create(UploadedFileModel, {
          ...originalFile,
          uploadingState: UploadingStateEnum.SUCCESS,
        });
      }

      return uploadedFile;
    });
  };

  // Удаляет файл из локального состояния
  removeFileFromState = (uploadedFiles: UploadedFileModel[], file: UploadedFileModel) => {
    return uploadedFiles.filter((uploadedFile) => uploadedFile.fileId !== file.fileId);
  };

  // Заполняем форму со всеми значениями false
  prepareImagesToDeletionFormValues = (data: UploadedFileModel[]) => {
    const preparedValues: Record<number, boolean> = {};

    data.forEach((file) => {
      if (!file.isCanDelete) {
        return;
      }

      Object.assign(preparedValues, { [file.fileId!]: false });
    });

    return {
      [AttachedImagesDeletionFormField.imagesToDelete]: preparedValues,
    };
  };

  prepareFileIdsToDeletion = (form: AttachedImagesDeletionForm) => {
    const fileIds = Object.entries(form.imagesToDelete).reduce((res: number[], [key, value]) => {
      if (value) {
        res.push(Number(key));
      }

      return res;
    }, []);

    return fileIds.join(',');
  };
}
